import { BarChart3, FileText, List, Plus, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import ContractAnalyticsDashboard from '../components/contracts/ContractAnalyticsDashboard';
import ContractDetails from '../components/contracts/ContractDetails';
import ContractForm from '../components/contracts/ContractForm';
import ContractsList from '../components/contracts/ContractsList';
import LoadingSpinner from '../components/LoadingSpinner';
import { usePermissions } from '../hooks/usePermissions';
import type { AppDispatch, RootState } from '../store';
import {
  fetchAllContracts,
  fetchContracts,
  setShowCreateModal,
} from '../store/slices/contractsSlice';

const Contracts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { contractId, id: urlProjectId } = useParams();
  const [searchParams] = useSearchParams();

  // Get projectId from URL params (projects/:id/contracts) or query params (?project=id)
  const projectId = urlProjectId || searchParams.get('project') || undefined;

  // Check user permissions
  const { contractPermissions } = usePermissions(projectId);

  const { contracts, loading, selectedContract, showCreateModal } = useSelector(
    (state: RootState) => state.contracts
  );

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all',
    projectId,
  });

  const [activeTab, setActiveTab] = useState<'list' | 'analytics'>('list');

  useEffect(() => {
    if (projectId) {
      // Fetch contracts for specific project
      dispatch(fetchContracts({ projectId, params: filters }));
    } else {
      // Fetch all contracts across projects (global view)
      dispatch(fetchAllContracts(filters));
    }
  }, [dispatch, projectId, filters]);

  const handleRefresh = () => {
    if (projectId) {
      dispatch(fetchContracts({ projectId, params: filters }));
    } else {
      dispatch(fetchAllContracts(filters));
    }
  };

  // Handle global contracts view when no specific project is selected
  const isGlobalView = !projectId;

  // Show loading spinner when loading contracts
  if (loading && contracts.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Show contract details if viewing a specific contract
  if (contractId && selectedContract) {
    return (
      <div className="absolute inset-0 flex flex-col">
        <ContractDetails />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Compact Header Bar */}
      <div className="flex-none bg-white border-b border-gray-200">
        <div className="px-5 py-2.5">
          <div className="flex items-center justify-between">
            {/* Left: Title + Inline Stats */}
            <div className="flex items-center space-x-5">
              <div className="flex items-center space-x-2.5">
                <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                  <FileText className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <h1 className="text-base font-semibold text-gray-900 leading-tight">
                    {isGlobalView ? 'All Contracts' : 'Contracts'}
                  </h1>
                  <p className="text-[11px] text-gray-500 leading-tight">
                    {isGlobalView
                      ? 'Contracts across all projects'
                      : 'Project contracts and agreements'}
                  </p>
                </div>
              </div>

              {/* Inline Stats - Compact */}
              {contracts.length > 0 && (
                <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                  <div className="flex items-center space-x-1">
                    <span className="text-xs font-semibold text-gray-900">{contracts.length}</span>
                    <span className="text-[10px] text-gray-400">Total</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              {/* Tab Switcher - Compact */}
              <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                <button
                  onClick={() => setActiveTab('list')}
                  className={`p-1.5 rounded transition-colors ${
                    activeTab === 'list'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="List View"
                >
                  <List className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`p-1.5 rounded transition-colors ${
                    activeTab === 'analytics'
                      ? 'bg-white text-brand-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Analytics View"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {/* Create Contract Button */}
              {contractPermissions.canCreate() && (
                <button
                  onClick={() => dispatch(setShowCreateModal(true))}
                  className="inline-flex items-center px-3 py-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md transition-colors"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  New Contract
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 p-4 bg-gray-50">
        <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 overflow-auto">
            {activeTab === 'list' && (
              <ContractsList filters={filters} onFiltersChange={setFilters} />
            )}
            {activeTab === 'analytics' && <ContractAnalyticsDashboard projectId={projectId} />}
          </div>
        </div>
      </div>

      {/* Contract Creation Modal */}
      <ContractForm
        isOpen={showCreateModal}
        onClose={() => dispatch(setShowCreateModal(false))}
        initialProjectId={projectId}
      />
    </div>
  );
};

export default Contracts;
